import { useMutation, useQuery, useQueryClient } from 'react-query';

import { parseErrors, Toast } from 'helpers';
import { Condition, JsonDataItem, Note, Technician } from 'types';
import { getTechnician, postTechnicianNote, putTechnician } from 'queries';

export const useTechnician = (technicianId?: string) => {
  const queryClient = useQueryClient();

  const { isLoading: isTechnicianLoading, data: tech } = useQuery(
    ['technician-data', { technicianId }],
    getTechnician,
    {
      enabled: !!technicianId,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const {
    mutate: updateTechnician,
    isLoading: updatingTechnician,
    data: updatedTech,
  } = useMutation(putTechnician, {
    onSuccess: (data: Technician) => {
      Toast.show({
        description: `New changes on technician file #${data?.ID} have been saved.`,
        type: Toast.Type.Success,
      });
      queryClient.setQueryData(['technician-data', { technicianId }], data);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const techData = updatedTech ?? tech;

  // Blacklist technician
  const { mutate: createBlacklistNote } = useMutation(postTechnicianNote, {
    onSuccess: () => {
      Toast.show({ description: 'Technician was successfully blacklisted.', type: Toast.Type.Success });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });
  const blacklistTechnician = (note: string, isBlacklisted: boolean) => {
    updateTechnician({
      ID: Number(technicianId),
      blacklist: isBlacklisted ? Condition.No : Condition.Yes,
      // pass mandatory fields to satisfy the api
      contact1_firstname: techData?.contact1_firstname,
      contact1_mobile: techData?.contact1_mobile,
      contact1_surname: techData?.contact1_surname,
      email: tech?.email,
      physical_address_line1: techData?.physical_address_line1,
      physical_postcode: techData?.physical_postcode,
      physical_state: techData?.physical_state,
      physical_suburb: techData?.physical_suburb,
      same_address: techData?.same_address,
      second_contact: techData?.second_contact,
      ...(techData?.second_contact === Condition.Yes ? { contact2_firstname: techData?.contact2_firstname } : null),
      ...(techData?.second_contact === Condition.Yes ? { contact2_mobile: techData?.contact2_mobile } : null),
      ...(techData?.second_contact === Condition.Yes ? { contact2_surname: techData?.contact2_surname } : null),
      ...(techData?.second_contact === Condition.Yes ? { contact2_title: techData?.contact2_title } : null),
      ...(techData?.same_address === Condition.No ? { postal_address_line1: techData?.postal_address_line1 } : null),
      ...(techData?.same_address === Condition.No ? { postal_postcode: techData?.postal_postcode } : null),
      ...(techData?.same_address === Condition.No ? { postal_state: techData?.postal_state } : null),
      ...(techData?.same_address === Condition.No ? { postal_suburb: techData?.postal_suburb } : null),
    });
    createBlacklistNote({
      techId: Number(technicianId),
      notes: note,
      note_type: Note.BlacklistTechnician,
    });
  };

  return {
    isLoading: isTechnicianLoading || updatingTechnician,
    technician: techData,
    updateTechnician,
    blacklistTechnician,
  };
};
